import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, Icon, InfoRow, PageHeader, PageLoader, Tabs, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { LedgerStatement } from '../../../components/common/LedgerStatement/LedgerStatement';
import { KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { RelatedList } from '../../../components/common/RelatedList/RelatedList';
import { useAction } from '../../../hooks/useAction';
import { useVendor } from '../hooks/useVendors';
import { vendorService } from '../services/vendorService';
import { RECORD_STATUS } from '../../../constants/status';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';

export default function VendorDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [tab, setTab] = useState('details');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: v, error, reload } = useVendor(id);
  useCrumb(`/vendors/${id}`, v?.name);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!v || !id) return <PageLoader />;

  const bal = v.balance ?? 0;
  const bank = v.bankDetails ?? {};
  const remove = async () => {
    if (await run('delete', () => vendorService.remove(id), `${v.name} deleted`)) navigate('/vendors', { replace: true });
    setConfirmDelete(false);
  };

  return (
    <>
      <PageHeader
        title={v.name}
        badge={v.isActive ? <Badge tone="green" dot>Active</Badge> : <Badge tone="grey" dot>Inactive</Badge>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="font-mono">{v.vendorCode}</span>
            {v.contactPerson && <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{v.contactPerson}</span>}
            {v.phone && <span className="inline-flex items-center gap-1.5"><Icon name="phone" size={15} />{v.phone}</span>}
            {v.address?.city && <span className="inline-flex items-center gap-1.5"><Icon name="mapPin" size={15} />{v.address.city}</span>}
          </span>
        }
        actions={
          <>
            <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Delete</Button>
            <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/vendors/${id}/edit`)}>Edit</Button>
            {bal > 0 && <Button variant="outline" leftIcon="rupee" onClick={() => navigate(`/transactions/new?category=purchase_payment&vendorId=${id}`)}>Pay bill</Button>}
            <Button leftIcon="plus" onClick={() => navigate(`/purchases/new?vendorId=${id}`)}>New purchase</Button>
          </>
        }
      />
      <TwoColumn
        asideFirst
        main={
          <>
            <Tabs
              label="Supplier sections"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: 'details', label: 'Details' },
                { value: 'purchases', label: 'Purchases' },
                { value: 'statement', label: 'Statement' },
              ]}
            />
            {tab === 'details' && (
              <Card className="p-5">
                <dl className="grid gap-5 sm:grid-cols-2">
                  <InfoRow label="Mobile">{v.phone}</InfoRow>
                  <InfoRow label="Email">{v.email}</InfoRow>
                  <InfoRow label="GSTIN">{v.gstin}</InfoRow>
                  <InfoRow label="Address">{[v.address?.line, v.address?.city, v.address?.pincode].filter(Boolean).join(', ')}</InfoRow>
                  <InfoRow label="Bank">{[bank.bankName, bank.accountNo, bank.ifsc].filter(Boolean).join(' · ')}</InfoRow>
                  <InfoRow label="UPI">{bank.upiId}</InfoRow>
                  {v.notes && <div className="sm:col-span-2"><InfoRow label="Notes">{v.notes}</InfoRow></div>}
                </dl>
              </Card>
            )}
            {tab === 'purchases' && (
              <RelatedList
                path="/purchases"
                query={{ vendorId: id }}
                statusMap={RECORD_STATUS}
                icon="cart"
                emptyTitle="No purchases yet"
                row={(p) => ({ to: `/purchases/${p._id}`, title: `${p.purchaseNo}${p.vendorBillNo ? ` · Bill ${p.vendorBillNo}` : ''}`, sub: formatDate(p.billDate), status: p.status, amount: money(p.grandTotal) })}
              />
            )}
            {tab === 'statement' && <LedgerStatement path={vendorService.ledgerPath(id)} chargeLabel="Bills" paymentLabel="Paid" emptyMessage="Purchases and payments to this supplier will appear here." />}
          </>
        }
        aside={
          <Card>
            <CardHeader title="Summary" />
            <div className="p-5 pt-4">
              <KeyValues rows={[['Total purchases', money(v.totalBilled)], ['Total paid', money(v.totalPaid)]]} />
              <div className="mt-4 flex items-center justify-between rounded-lg bg-brand-50/70 px-4 py-3">
                <span className="text-sm font-medium text-navy-900">{bal > 0 ? 'You owe' : bal < 0 ? 'Advance with them' : 'All settled'}</span>
                <span className="tabular text-xl font-semibold text-brand-700">{money(Math.abs(bal))}</span>
              </div>
            </div>
          </Card>
        }
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title="Delete this supplier?"
        message="They will be removed from lists. Old purchases stay saved. Suppliers with a pending balance can't be deleted."
        confirmLabel="Delete supplier"
      />
    </>
  );
}
